import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import PredictionCard from '../components/analysts/PredictionCard';
import CoverageSymbolRow from '../components/analysts/CoverageSymbolRow';
import CommentarySymbolRow from '../components/analysts/CommentarySymbolRow';
import CommentaryTreemap from '../components/analysts/CommentaryTreemap';
import BestCallCard from '../components/analysts/BestCallCard';
import SectorEdgeTab from '../components/analysts/SectorEdgeTab';
import QuotaUpgradeModal from '../components/ui/QuotaUpgradeModal';
import useFollowAnalyst from '../hooks/useFollowAnalyst';
import ShareModal from '../components/ui/ShareModal';
import { ArrowLeft, Twitter, Youtube, BookOpen, Radio, Globe, Heart, Loader2, Share2 } from 'lucide-react';

const PLATFORM_ICONS = {
  twitter: Twitter,
  youtube: Youtube,
  substack: BookOpen,
  podcast: Radio,
};

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ReturnBadge({ value }) {
  if (value == null) return <span className="font-bold text-xl text-gray-500">N/A</span>;
  const n = parseFloat(value);
  if (n === 0) return <span className="font-bold text-xl text-gray-500">N/A</span>;
  const color = n > 0 ? 'text-emerald-600' : 'text-red-600';
  return <span className={`font-bold text-xl ${color}`}>{n >= 0 ? '+' : ''}{n.toFixed(1)}%</span>;
}

const TABS = ['Predictions', 'Prediction Coverage', 'Commentary Coverage', 'Best Calls', 'Sector Edge'];

export default function AnalystProfilePage() {
  const { slug } = useParams();
  const [analyst, setAnalyst] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [coverageLoaded, setCoverageLoaded] = useState(false);
  const [commentary, setCommentary] = useState([]);
  const [commentaryLoaded, setCommentaryLoaded] = useState(false);
  const [bestCalls, setBestCalls] = useState([]);
  const [bestCallsLoaded, setBestCallsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Predictions');
  const [returnPeriod, setReturnPeriod] = useState('current');
  const isGuest = !localStorage.getItem('token');
  const [quotaError, setQuotaError] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { following, toggle: toggleFollow, loading: followLoading, error: followError } = useFollowAnalyst(
    slug,
    analyst?.is_following ?? false,
  );

  useEffect(() => {
    const headers = getAuthHeader();

    const handleQuotaError = (err) => {
      if (err.response?.status === 402) {
        setQuotaError(err.response.data);
      }
    };

    const predsPromise = axios.get(`${API_BASE_URL}/api/research/analysts/${slug}/predictions/`, { headers })
      .catch((err) => {
        handleQuotaError(err);
        return { data: [] };
      });

    Promise.all([
      axios.get(`${API_BASE_URL}/api/research/analysts/${slug}/`, { headers })
        .catch((err) => { handleQuotaError(err); return { data: null }; }),
      predsPromise,
    ])
      .then(([analystRes, predsRes]) => {
        setAnalyst(analystRes.data);
        const predsData = predsRes.data;
        setPredictions(Array.isArray(predsData) ? predsData : predsData?.results ?? []);
      })
      .catch((err) => console.error('Failed to load analyst profile', err))
      .finally(() => setLoading(false));
  }, [slug]);

  // Lazy-load prediction coverage when tab is first opened
  useEffect(() => {
    if (activeTab !== 'Prediction Coverage' || coverageLoaded) return;
    const headers = getAuthHeader();
    axios
      .get(`${API_BASE_URL}/api/research/analysts/${slug}/coverage/`, { headers })
      .then((res) => { setCoverage(res.data); setCoverageLoaded(true); })
      .catch((err) => console.error('Failed to load coverage', err));
  }, [activeTab, coverageLoaded, slug]);

  // Lazy-load commentary coverage when tab is first opened
  useEffect(() => {
    if (activeTab !== 'Commentary Coverage' || commentaryLoaded) return;
    const headers = getAuthHeader();
    axios
      .get(`${API_BASE_URL}/api/research/analysts/${slug}/commentary-coverage/`, { headers })
      .then((res) => { setCommentary(res.data); setCommentaryLoaded(true); })
      .catch((err) => console.error('Failed to load commentary coverage', err));
  }, [activeTab, commentaryLoaded, slug]);

  // Lazy-load best calls when tab is first opened
  useEffect(() => {
    if (activeTab !== 'Best Calls' || bestCallsLoaded) return;
    const headers = getAuthHeader();
    axios
      .get(`${API_BASE_URL}/api/research/analysts/${slug}/best_calls/`, { headers })
      .then((res) => { setBestCalls(res.data); setBestCallsLoaded(true); })
      .catch((err) => console.error('Failed to load best calls', err));
  }, [activeTab, bestCallsLoaded, slug]);

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="animate-pulse max-w-4xl mx-auto">
          <div className="h-6 bg-gray-200 rounded w-24 mb-6" />
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-gray-200" />
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-48 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-64" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analyst) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        Analyst not found.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back link */}
        <Link
          to="/leaderboard"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
        >
          <ArrowLeft size={15} />
          All Analysts
        </Link>

        {/* Profile header */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-2xl font-bold text-gray-600 flex-shrink-0 overflow-hidden">
              {analyst.profile_image_url
                ? <img src={analyst.profile_image_url} alt={analyst.name} className="w-full h-full object-cover" />
                : analyst.name?.[0]}
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-3 mb-1">
                <h1 className="text-xl font-bold text-gray-900">{analyst.name}</h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setIsShareModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border bg-white border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-all"
                  >
                    <Share2 size={14} />
                    Share
                  </button>
                  <button
                    onClick={toggleFollow}
                    disabled={followLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all flex-shrink-0 ${
                      following
                        ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {followLoading
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Heart size={14} className={following ? 'fill-red-500 text-red-500' : ''} />}
                    {following ? 'Following' : 'Follow'}
                  </button>
                </div>
              </div>
              {followError && (
                <p className="text-xs text-red-500 mb-2">{followError}</p>
              )}
              {analyst.bio && (
                <p className="text-sm text-gray-500 mb-3">{analyst.bio}</p>
              )}
              {/* Content sources / channels */}
              <div className="flex items-center gap-2 flex-wrap">
                {analyst.content_sources?.map((src) => {
                  const Icon = PLATFORM_ICONS[src.source_type] || Globe;
                  return (
                    <a
                      key={src.id}
                      href={src.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      <Icon size={12} />
                      {src.name}
                    </a>
                  );
                })}
              </div>
              {/* Focus tags */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {analyst.focus_countries?.map((c) => (
                  <span key={c} className="text-xs bg-gray-50 border border-gray-200 text-gray-500 px-2 py-0.5 rounded-full">{c}</span>
                ))}
                {analyst.focus_asset_classes?.map((a) => (
                  <span key={a} className="text-xs bg-blue-50 border border-blue-100 text-blue-600 px-2 py-0.5 rounded-full">{a}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Stats — row 1: core */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-50">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{analyst.predictions_count}</div>
              <div className="text-xs text-gray-400 mt-0.5">Predictions</div>
            </div>
            <div className="text-center">
              <ReturnBadge value={analyst.first_call_return_pct ?? analyst.overall_return_pct} />
              <div className="text-xs text-gray-400 mt-0.5">First Call Return</div>
            </div>
            <div className="text-center">
              <ReturnBadge value={analyst.follower_return_pct ?? analyst.overall_return_pct} />
              <div className="text-xs text-gray-400 mt-0.5">Follower Return</div>
            </div>
            <div className="text-center">
              <ReturnBadge value={analyst.median_return_pct} />
              <div className="text-xs text-gray-400 mt-0.5">Median Return</div>
            </div>
            <div className="text-center">
              <ReturnBadge value={analyst.trimmed_return_pct} />
              <div className="text-xs text-gray-400 mt-0.5">Avg (no extremes)</div>
            </div>
          </div>

          {/* Stats — row 2: time-window returns */}
          <div className="grid grid-cols-5 gap-3 mt-4 pt-4 border-t border-gray-50">
            {[
              { label: '7D Avg', value: analyst.avg_return_7d },
              { label: '1M Avg', value: analyst.avg_return_1m },
              { label: '3M Avg', value: analyst.avg_return_3m },
              { label: '6M Avg', value: analyst.avg_return_6m },
              { label: '1Y Avg', value: analyst.avg_return_1y },
            ].map(({ label, value }) => (
              <div key={label} className="text-center">
                <ReturnBadge value={value} />
                <div className="text-xs text-gray-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab}
              {tab === 'Predictions' && (
                <span className="ml-1.5 text-xs text-gray-400">({predictions.length})</span>
              )}
              {tab === 'Prediction Coverage' && coverageLoaded && (
                <span className="ml-1.5 text-xs text-gray-400">({coverage.length})</span>
              )}
              {tab === 'Commentary Coverage' && commentaryLoaded && (
                <span className="ml-1.5 text-xs text-gray-400">({commentary.length})</span>
              )}
              {tab === 'Best Calls' && bestCallsLoaded && (
                <span className="ml-1.5 text-xs text-gray-400">({bestCalls.length})</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'Predictions' && (
          <>
            {/* Period chip row */}
            <div className="flex items-center gap-1.5 mb-4 flex-wrap">
              {[
                { value: 'current', label: 'Current' },
                { value: '7d',  label: '7D' },
                { value: '1m',  label: '1M' },
                { value: '3m',  label: '3M' },
                { value: '6m',  label: '6M' },
                { value: '1y',  label: '1Y' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setReturnPeriod(value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    returnPeriod === value
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {label}
                </button>
              ))}
              <span className="text-xs text-gray-400 ml-1">Return shown on cards</span>
            </div>

            {predictions.length === 0 ? (
              <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-100">
                No predictions recorded yet.
              </div>
            ) : (
              <div className="relative">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {predictions.map((pred) => (
                    <PredictionCard key={pred.id} prediction={pred} returnPeriod={returnPeriod} />
                  ))}
                </div>
                {isGuest && (
                  <div className="absolute inset-0 backdrop-blur-sm bg-white/60 rounded-xl flex flex-col items-center justify-center gap-3">
                    <p className="text-sm font-medium text-gray-700">Sign in to see predictions</p>
                    <Link
                      to="/login"
                      className="px-5 py-2 bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white rounded-lg font-semibold text-sm shadow hover:brightness-110 active:scale-95 transition-all"
                    >
                      Sign In
                    </Link>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'Best Calls' && (
          <>
            {!bestCallsLoaded ? (
              <div className="text-center py-10 text-gray-400 animate-pulse">
                Loading best calls…
              </div>
            ) : bestCalls.length === 0 ? (
              <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-100">
                No best calls available yet.
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {bestCalls.map((call, idx) => (
                  <BestCallCard key={call.id ?? idx} call={call} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'Prediction Coverage' && (
          <>
            {!coverageLoaded ? (
              <div className="text-center py-10 text-gray-400 animate-pulse">
                Loading coverage data…
              </div>
            ) : coverage.length === 0 ? (
              <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-100">
                No coverage data available yet.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {coverage.map((item) => (
                  <CoverageSymbolRow
                    key={item.ticker}
                    item={item}
                    analystName={analyst.name}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'Commentary Coverage' && (
          <>
            {!commentaryLoaded ? (
              <div className="text-center py-10 text-gray-400 animate-pulse">
                Loading commentary data…
              </div>
            ) : commentary.length === 0 ? (
              <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-100">
                No commentary data available yet.
              </div>
            ) : (
              <>
                <CommentaryTreemap
                  items={commentary}
                  onTileClick={(ticker) => {
                    const el = document.getElementById(`commentary-row-${ticker}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                />
                <div className="flex flex-col gap-3 mt-4">
                  {commentary.map((item) => (
                    <div key={item.ticker} id={`commentary-row-${item.ticker}`}>
                      <CommentarySymbolRow item={item} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'Sector Edge' && (
          <SectorEdgeTab slug={slug} />
        )}

        <QuotaUpgradeModal
          quotaError={quotaError}
          onClose={() => setQuotaError(null)}
        />

        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title="Share Profile"
          imageUrl={analyst.og_image_url || `${API_BASE_URL}/api/research/og/analyst/${analyst.slug}/`}
          downloadUrl={`${API_BASE_URL}/api/research/og/analyst/${analyst.slug}/?download=true`}
          shareText={`Check out ${analyst.name}'s verified track record on TraderBro!`}
        />
      </div>
    </div>
  );
}
